// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Single navigation arrow for the `EmblaCarousel`.
 *
 * Reads the carousel from {@link CarouselContext}, picks the appropriate icon
 * for the current direction (up/down for vertical, chevrons for horizontal),
 * and wires up click + disabled state via {@link useArrowElement}.
 */

import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import { getProp } from "@insite/mobius/utilities";
import { useCarouselContext } from "@stoneage/App/Components/EmblaCarousel/CarouselContext";
import { useArrowElement } from "@stoneage/App/Components/EmblaCarousel/hooks";
import { ArrowPosition, NavigationProps } from "@stoneage/App/Components/EmblaCarousel/models";
import { getArrowPosition } from "@stoneage/App/Components/EmblaCarousel/utils";
import { color, xClassNames, xScreen } from "@stoneage/XDL/Utilities";
import React, { FC, useCallback } from "react";
import { css } from "styled-components";
import CarouselLeftArrowIcon from "@stoneage/XDL/Icons/CarouselLeftArrowIcon";
import CarouselRightArrowIcon from "@stoneage/XDL/Icons/CarouselRightArrowIcon";

const ARROW_ICON_SIZE = 52;

const arrowIcons: Record<ArrowPosition, { src: any; size: number }> = {
    top: { src: ArrowUp, size: ARROW_ICON_SIZE },
    bottom: { src: ArrowDown, size: ARROW_ICON_SIZE },
    left: { src: CarouselLeftArrowIcon, size: ARROW_ICON_SIZE },
    right: { src: CarouselRightArrowIcon, size: ARROW_ICON_SIZE },
};

/** Build the inline CSS passed to the `Clickable` for a given arrow position. */
const generateArrowStyle = (arrowPosition: ArrowPosition, navigationOutSide?: boolean) => ({
    size: ARROW_ICON_SIZE,
    css: css`
        position: static;
        margin-${arrowPosition === "left" ? "right" : "left"}: 20px;
        background: transparent;
        transform: none;
        z-index: 10;
        padding: 0;
        border: 0;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        outline: none !important;
        cursor: pointer;
        & > ${IconWrapper} {
            color: ${color("primary.main")};
            transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
        }

        &:hover > ${IconWrapper} {
            color: ${color("common.ui1")};
        }

        &:focus {
            box-shadow: none;
        }

        ${
            (arrowPosition === "top" || arrowPosition === "bottom") &&
            css`
                position: relative;
                inset: 0;
                transform: none;
                margin: auto;
            `
        }
        ${
            (arrowPosition === "left" || arrowPosition === "right") &&
            navigationOutSide &&
            css`
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                margin: 0;
                ${arrowPosition}: 0;
            `
        }

        &.carousel-button-disabled > ${IconWrapper} {
           display: none;
        }

        ${xScreen("lte", "sm")} {
           &.arrow-on-desktop {
                display: none;
            }         
        }
    `,
});

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({ arrowType, showOnMobile = true, buttonIconProps, navigationOutside, ...otherProps }) => {
        const ctx = useCarouselContext();
        const carouselApi = ctx?.carouselApi ?? null;
        const direction = ctx?.direction ?? "horizontal";

        const arrowPosition = getArrowPosition(arrowType, direction);
        const { src: iconSrc, size: iconSize } = arrowIcons[arrowPosition];

        const { className, disabled } = useArrowElement({
            carousel: carouselApi,
            showOnMobile,
            arrowType,
        });

        const onClick = useCallback(
            (event: React.MouseEvent<HTMLButtonElement>) => {
                if (!carouselApi || disabled) {
                    return;
                }

                if (arrowType === "next") {
                    carouselApi.scrollNext();
                } else {
                    carouselApi.scrollPrev();
                }

                otherProps.onClick?.(event);
            },
            [arrowType, carouselApi, disabled, otherProps.onClick],
        );

        if (!carouselApi) {
            return null;
        }

        return (
            <Clickable
                {...{ arrowPosition, showOnMobile }}
                className={xClassNames.append(className, otherProps.className)}
                {...generateArrowStyle(arrowPosition, navigationOutside)}
                aria-label={`carousel-${arrowPosition}`}
                onClick={onClick}
                data-test-selector={`carousel-${arrowPosition}-arrow-button`}
            >
                <ButtonIcon
                    src={iconSrc}
                    size={iconSize}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon${disabled ? " disabled" : ""}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

ArrowNavigation.displayName = "ArrowNavigation";

export default ArrowNavigation;
